import React, { useMemo, useState } from "react";
import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AdminFixStripeProduct from "../../components/AdminFixStripeProduct";
import AdminResyncFromStripe from "../../components/AdminResyncFromStripe";
import {
  getMembershipStatusPresentation,
  isPendingCancellation,
} from "../../utils/subscriptionDisplayStatus";

function formatExpiry(validUntilDateUTC) {
  if (!validUntilDateUTC) return null;
  const d = new Date(validUntilDateUTC);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function getSubscriptionStatus(subscription) {
  const now = new Date();
  const isExpired =
    subscription.validUntilDateUTC && new Date(subscription.validUntilDateUTC) < now;
  const isFuture =
    subscription.validFromDateUTC && new Date(subscription.validFromDateUTC) > now;
  if (isExpired) return { label: "Expired", color: "default" };
  if (isFuture) return { label: "Upcoming", color: "warning" };
  const presentation = getMembershipStatusPresentation(subscription);
  if (isPendingCancellation(subscription)) {
    return { label: "Pending cancellation", color: "warning" };
  }
  if (subscription.active) return { label: "Active", color: "success" };
  if (presentation.statusLabel === "Payment required") {
    return { label: "Payment required", color: "error" };
  }
  return { label: presentation.statusLabel || "Inactive", color: "error" };
}

function groupSubscriptions(subscriptions) {
  const allById = new Map(
    subscriptions.map((sub) => [String(sub._id), sub])
  );
  const groups = [];
  const addOnsByMain = new Map();

  subscriptions.forEach((sub) => {
    const isAddOn = sub.note && sub.note.mainSubscriptionId;
    if (!isAddOn) return;
    const mainId = String(sub.note.mainSubscriptionId);
    if (!addOnsByMain.has(mainId)) addOnsByMain.set(mainId, []);
    addOnsByMain.get(mainId).push(sub);
  });

  const usedMainIds = new Set();

  subscriptions.forEach((sub) => {
    const isAddOn = sub.note && sub.note.mainSubscriptionId;
    if (isAddOn) return;

    const mainId = String(sub._id);
    usedMainIds.add(mainId);
    groups.push({
      main: sub,
      addOns: addOnsByMain.get(mainId) || [],
    });
  });

  subscriptions.forEach((sub) => {
    const isAddOn = sub.note && sub.note.mainSubscriptionId;
    if (!isAddOn) return;
    const mainId = String(sub.note.mainSubscriptionId);
    if (!allById.has(mainId) && !usedMainIds.has(`orphan-${mainId}`)) {
      groups.push({ main: null, addOns: addOnsByMain.get(mainId) || [sub], orphanMainId: mainId });
      usedMainIds.add(`orphan-${mainId}`);
    }
  });

  return groups;
}

function AdminSubscriptionCard({ subscription, vehicle, compact = false, onRefresh }) {
  const navigate = useNavigate();
  if (!subscription) return null;

  const isAddOn = subscription.note && subscription.note.mainSubscriptionId;
  const status = getSubscriptionStatus(subscription);
  const now = new Date();
  const isExpired =
    subscription.validUntilDateUTC && new Date(subscription.validUntilDateUTC) < now;
  const isFuture =
    subscription.validFromDateUTC && new Date(subscription.validFromDateUTC) > now;
  const showQr = !isExpired && !isFuture && subscription.qrcode;
  const productName =
    subscription.product?.name || (isAddOn ? "Add-on" : "Subscription");

  return (
    <div
      className={`flex flex-col bg-white border rounded-lg cursor-pointer hover:shadow-md transition-shadow ${
        compact ? "p-3 min-w-[200px] max-w-[240px]" : "p-4 w-full max-w-[320px]"
      } ${isAddOn ? "border-purple-300 border-l-4" : "border-blue-300 border-l-4"}`}
      onClick={() => navigate(`/subscription/${subscription._id}`)}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex flex-wrap gap-1">
          <Chip
            size="small"
            label={isAddOn ? "Add-on" : "Main"}
            sx={{
              bgcolor: isAddOn ? "#f3e8ff" : "#dbeafe",
              color: isAddOn ? "#6b21a8" : "#1d4ed8",
              fontWeight: 600,
            }}
          />
          <Chip size="small" label={status.label} color={status.color} variant="outlined" />
        </div>
      </div>

      <Typography variant={compact ? "body2" : "subtitle1"} fontWeight={600} sx={{ mb: 0.5 }}>
        {productName}
      </Typography>

      {!compact && vehicle && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          {vehicle.rego || "No rego"} · {vehicle.vin || "No VIN"}
        </Typography>
      )}

      <div className="flex justify-center my-2">
        {showQr ? (
          <img
            src={subscription.qrcode}
            alt="Subscription QR"
            className={compact ? "w-24 h-24 object-contain" : "w-36 h-36 object-contain"}
          />
        ) : (
          <div
            className={`bg-gray-100 text-center text-xs text-gray-500 rounded flex items-center justify-center ${
              compact ? "w-24 h-24" : "w-36 h-36"
            }`}
          >
            {isExpired ? "Expired" : isFuture ? "Not yet valid" : "No QR code"}
          </div>
        )}
      </div>

      {subscription.validUntilDateUTC && (
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", mb: 1 }}>
          Expires: {formatExpiry(subscription.validUntilDateUTC)}
        </Typography>
      )}

      {subscription.stripeId && !compact && (
        <div className="flex justify-center mt-1" onClick={(e) => e.stopPropagation()}>
          <AdminFixStripeProduct
            subscriptionId={subscription._id}
            hasStripeId={!!subscription.stripeId}
            onSuccess={onRefresh}
          />
        </div>
      )}

      <Typography variant="caption" color="text.disabled" sx={{ mt: 1, wordBreak: "break-all" }}>
        {subscription._id}
      </Typography>
    </div>
  );
}

const AdminCustomerSubscriptions = ({ vehicles = [], onRefresh }) => {
  const [vehicleFilter, setVehicleFilter] = useState("all");

  const vehiclesWithSubs = useMemo(
    () => (vehicles || []).filter((v) => v?.activeSubscriptions?.length > 0),
    [vehicles]
  );

  const subscriptionsWithVehicle = useMemo(() => {
    const rows = [];
    vehiclesWithSubs.forEach((vehicle) => {
      (vehicle.activeSubscriptions || []).forEach((sub) => {
        rows.push({ subscription: sub, vehicle });
      });
    });
    return rows;
  }, [vehiclesWithSubs]);

  const filteredRows = useMemo(() => {
    if (vehicleFilter === "all") return subscriptionsWithVehicle;
    return subscriptionsWithVehicle.filter(
      (row) => String(row.vehicle._id) === String(vehicleFilter)
    );
  }, [subscriptionsWithVehicle, vehicleFilter]);

  const groupedByVehicle = useMemo(() => {
    const byVehicle = new Map();
    filteredRows.forEach(({ subscription, vehicle }) => {
      const vid = String(vehicle._id);
      if (!byVehicle.has(vid)) {
        byVehicle.set(vid, { vehicle, subscriptions: [] });
      }
      byVehicle.get(vid).subscriptions.push(subscription);
    });

    return Array.from(byVehicle.values()).map(({ vehicle, subscriptions }) => ({
      vehicle,
      groups: groupSubscriptions(subscriptions),
      stripeSubs: subscriptions.filter((s) => s.stripeId),
    }));
  }, [filteredRows]);

  const stripeSubsForFilter = useMemo(() => {
    if (vehicleFilter === "all") {
      return subscriptionsWithVehicle
        .filter((r) => r.subscription.stripeId)
        .map((r) => ({ subscription: r.subscription, vehicle: r.vehicle }));
    }
    const vehicle = vehiclesWithSubs.find((v) => String(v._id) === String(vehicleFilter));
    if (!vehicle) return [];
    return (vehicle.activeSubscriptions || [])
      .filter((s) => s.stripeId)
      .map((subscription) => ({ subscription, vehicle }));
  }, [vehicleFilter, subscriptionsWithVehicle, vehiclesWithSubs]);

  if (!vehiclesWithSubs.length) {
    return <p className="text-sm text-gray-500">No subscriptions for this customer.</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="admin-sub-vehicle-filter-label">Filter by vehicle</InputLabel>
          <Select
            labelId="admin-sub-vehicle-filter-label"
            value={vehicleFilter}
            label="Filter by vehicle"
            onChange={(e) => setVehicleFilter(e.target.value)}
          >
            <MenuItem value="all">All vehicles ({vehiclesWithSubs.length})</MenuItem>
            {vehiclesWithSubs.map((vehicle) => (
              <MenuItem key={vehicle._id} value={vehicle._id}>
                {vehicle.rego || "No rego"} ({vehicle.activeSubscriptions.length} sub
                {vehicle.activeSubscriptions.length !== 1 ? "s" : ""})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {stripeSubsForFilter.length > 0 && (
          <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
            {stripeSubsForFilter.map(({ subscription, vehicle }) => (
              <AdminResyncFromStripe
                key={subscription._id}
                subscriptionId={subscription._id}
                hasStripeId
                size="small"
                buttonLabel={
                  vehicleFilter === "all"
                    ? `Resync (${vehicle.rego || "?"})`
                    : "Resync from Stripe"
                }
                onSuccess={onRefresh}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-6">
        {groupedByVehicle.map(({ vehicle, groups }) => (
          <div key={vehicle._id} className="flex flex-col gap-4">
            {vehicleFilter === "all" && (
              <div className="flex items-center gap-2 pb-1 border-b border-gray-200">
                <Typography variant="subtitle1" fontWeight={700}>
                  {vehicle.rego || "No rego"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {vehicle.vin || "No VIN"}
                </Typography>
                <Chip
                  size="small"
                  label={
                    vehicle.active !== false
                      ? "Active vehicle"
                      : "Awaiting customer activation"
                  }
                  color={vehicle.active !== false ? "success" : "warning"}
                  variant="outlined"
                />
              </div>
            )}

            {groups.map((group, idx) => (
              <div key={group.main?._id || group.orphanMainId || idx} className="flex flex-col gap-3">
                {group.main ? (
                  <AdminSubscriptionCard
                    subscription={group.main}
                    vehicle={vehicle}
                    onRefresh={onRefresh}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Add-ons linked to main subscription {group.orphanMainId} (main not in current list)
                  </Typography>
                )}

                {group.addOns.length > 0 && (
                  <div className="ml-4 sm:ml-8 pl-4 border-l-2 border-purple-200">
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                      {group.addOns.length} add-on{group.addOns.length !== 1 ? "s" : ""} included
                    </Typography>
                    <div className="flex flex-wrap gap-3">
                      {group.addOns.map((addOn) => (
                        <AdminSubscriptionCard
                          key={addOn._id}
                          subscription={addOn}
                          vehicle={vehicle}
                          compact
                          onRefresh={onRefresh}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminCustomerSubscriptions;
